
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, Lock, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
// import { supabase } from '@/integrations/supabase/client';
import Footer from '@/components/layout/Footer';
import Header from '@/components/layout/Header';
import PageTransition from '@/components/ui/PageTransition';

const PublicProfile = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [biodata, setBiodata] = useState(null);
  const [visibleFields, setVisibleFields] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Commented out Supabase-specific code, needs to be replaced with Azure/Firebase
    // const fetchPublicProfile = async () => {
    //   try {
    //     setLoading(true);
    //     
    //     // Fetch the public profile by slug - Needs Azure/DB implementation
    //     // const { data: publicProfile, error: profileError } = await supabase
    //     //   .from('public_profiles')
    //     //   .select('*')
    //     //   .eq('slug', slug)
    //     //   .eq('active', true)
    //     //   .single();
    //     //   
    //     // if (profileError) throw profileError;
    //     // if (!publicProfile) throw new Error('Profile not found');
    //     // 
    //     // setProfile(publicProfile);
    //     // 
    //     // // Fetch the visible fields for this profile
    //     // const { data: fields, error: fieldsError } = await supabase
    //     //   .from('public_profile_fields')
    //     //   .select('field_name')
    //     //   .eq('public_profile_id', publicProfile.id)
    //     //   .eq('visible', true);
    //     //   
    //     // if (fieldsError) throw fieldsError;
    //     // 
    //     // setVisibleFields(fields.map(f => f.field_name));
    //     // 
    //     // // Fetch the biodata
    //     // const { data: biodataResult, error: biodataError } = await supabase
    //     //   .from('biodata')
    //     //   .select('*')
    //     //   .eq('id', publicProfile.biodata_id)
    //     //   .single();
    //     //   
    //     // if (biodataError) throw biodataError;
    //     // 
    //     // setBiodata(biodataResult);
    //   } catch (error) {
    //     console.error('Error fetching public profile:', error);
    //     setError(error.message);
    //   } finally {
    //     setLoading(false);
    //   }
    // };
    
    // if (slug) {
    //   fetchPublicProfile();
    // }
    // For now, set error to show the 'Profile Not Available' message
    setError('Profile loading functionality temporarily disabled');
    setLoading(false);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="h-12 w-12 rounded-full bg-primary/10 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !biodata) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <Lock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Profile Not Available</h1>
          <p className="text-muted-foreground mb-6">
            This profile may have expired, been deactivated, or doesn't exist.
          </p>
          <Button asChild>
            <a href="/">Go to Homepage</a>
          </Button>
        </div>
      </div>
    );
  }

  // Determine which fields to display based on visibleFields
  const renderField = (name, value) => {
    if (!visibleFields.includes(name) || !value) return null;
    
    return (
      <div>
        <p className="text-sm text-muted-foreground">{name.charAt(0).toUpperCase() + name.slice(1).replace('_', ' ')}</p>
        <p className="font-medium">{value}</p>
      </div>
    );
  };

  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col">
        <Header />
        
        <main className="flex-grow pt-28 pb-20">
          <div className="container px-6 mx-auto">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
                  <div>
                    <div className="flex items-center mb-2">
                      <Eye className="h-5 w-5 text-primary mr-2" />
                      <h1 className="text-3xl font-bold">Public Profile</h1>
                    </div>
                    <p className="text-muted-foreground">Viewing shared information</p>
                  </div>
                </div>
                
                <div className="bg-secondary/30 rounded-xl p-4 mb-8 flex items-center">
                  <Eye className="h-5 w-5 text-primary mr-2" />
                  <p className="text-sm">You're viewing a public profile with limited information chosen by the owner.</p>
                </div>
                
                {/* Profile Preview */}
                <div className="bg-white rounded-xl shadow-sm border border-muted overflow-hidden">
                  {/* Profile Header */}
                  <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-muted flex items-center justify-center border-4 border-white">
                        <span className="text-2xl text-muted-foreground">
                          {biodata.full_name ? biodata.full_name.charAt(0) : "?"}
                        </span>
                      </div>
                      <div className="flex-1">
                        {renderField('full_name', biodata.full_name)}
                        
                        <div className="flex flex-wrap gap-2 mt-2">
                          {renderField('religion', biodata.religion) && (
                            <span className="bg-primary/10 text-primary text-xs font-medium px-2 py-1 rounded-full">
                              {biodata.religion}
                            </span>
                          )}
                          {renderField('marital_status', biodata.marital_status) && (
                            <span className="bg-primary/10 text-primary text-xs font-medium px-2 py-1 rounded-full">
                              {biodata.marital_status}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Personal Information */}
                  <div className="p-6 border-b border-muted">
                    <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6">
                      {renderField('full_name', biodata.full_name)}
                      {renderField('gender', biodata.gender)}
                      {renderField('religion', biodata.religion)}
                      {renderField('marital_status', biodata.marital_status)}
                      {renderField('date_of_birth', biodata.date_of_birth && new Date(biodata.date_of_birth).toLocaleDateString())}
                    </div>
                  </div>
                  
                  {/* Contact Information - Always shown as Private in public view */}
                  <div className="p-6 border-b border-muted">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Contact Information</h3>
                      <span className="bg-muted text-muted-foreground text-xs px-2 py-1 rounded-full flex items-center">
                        <Lock className="h-3 w-3 mr-1" />
                        Private
                      </span>
                    </div>
                    <div className="bg-muted/30 rounded-lg p-4 flex items-center justify-center">
                      <div className="text-center">
                        <Lock className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-muted-foreground">Contact information is private</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <Button asChild variant="outline" className="rounded-full">
                    <a href="/">
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Back to Homepage
                    </a>
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
    </PageTransition>
  );
};

export default PublicProfile;
